import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, onAuthStateChanged, signOut as fbSignOut } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import type { Member } from '@shared/types';

interface AuthContextType {
  user: User | null;
  member: Member | null;
  loading: boolean;
  isAdmin: boolean;
  isTreasurer: boolean;
  isAuditor: boolean;
  isStaff: boolean;
  isActive: boolean;
  emailVerified: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [member, setMember] = useState<Member | null>(null);
  const [adminRole, setAdminRole] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let unsubMember: (() => void) | null = null;
    let unsubAdmin: (() => void) | null = null;

    const unsubAuth = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);

      if (firebaseUser) {
        // Listen to member profile in Firestore (cached offline via IndexedDB)
        const memberRef = doc(db, 'members', firebaseUser.uid);
        unsubMember = onSnapshot(
          memberRef,
          (snap) => {
            if (snap.exists()) {
              setMember(snap.data() as Member);
            } else {
              setMember(null);
            }
          },
          (err) => {
            console.warn('[FHPF] Member snapshot read offline or error:', err.message);
          }
        );

        // Listen to admin role if exists
        const adminRef = doc(db, 'admins', firebaseUser.uid);
        unsubAdmin = onSnapshot(
          adminRef,
          (snap) => {
            if (snap.exists()) {
              setAdminRole(snap.data().role || null);
            } else {
              setAdminRole(null);
            }
            setLoading(false);
          },
          () => {
            setAdminRole(null);
            setLoading(false);
          }
        );
      } else {
        setMember(null);
        setAdminRole(null);
        setLoading(false);
      }
    });

    return () => {
      unsubAuth();
      if (unsubMember) unsubMember();
      if (unsubAdmin) unsubAdmin();
    };
  }, []);

  const signOut = async () => {
    await fbSignOut(auth);
  };

  const isAdmin = adminRole === 'admin';
  const isTreasurer = adminRole === 'treasurer';
  const isAuditor = adminRole === 'auditor';
  const isStaff = isAdmin || isTreasurer || isAuditor;
  const isActive = member?.status === 'active';
  const emailVerified = user?.emailVerified ?? false;

  return (
    <AuthContext.Provider
      value={{
        user,
        member,
        loading,
        isAdmin,
        isTreasurer,
        isAuditor,
        isStaff,
        isActive,
        emailVerified,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
